// src/components/admin/Badge.tsx
import { type LucideIcon } from 'lucide-react';
import { type ReactNode } from 'react';

import { cn } from '@/lib/utils';

export type BadgeTone = 'neutral' | 'primary' | 'info' | 'warning' | 'success' | 'danger';

export interface BadgeProps {
  tone: BadgeTone;
  children: ReactNode;
  uppercase?: boolean;
  icon?: LucideIcon;
  /**
   * Adds a small leading dot — useful for status chips (Active / Inactive /
   * Pending) where the colored dot doubles as a status affordance and pulls
   * the chip closer to the StatusPill recipe used in the detail-screen heroes.
   */
  dot?: boolean;
  className?: string;
}

// Pill recipe: filled tinted bg + 1px tinted ring. Mirrors the StatusPill
// composition introduced in PractitionerDetailScreen so chips read as one
// design language across list/table cells and hero zones.
const TONE_CLASSES: Record<BadgeTone, string> = {
  neutral: 'bg-neutral-100 text-neutral-700 ring-neutral-200/70',
  primary: 'bg-primary-50 text-primary-700 ring-primary-100/70',
  info: 'bg-[#e8f0f5] text-accent-ink ring-[#cdd9e2]/60',
  warning: 'bg-warning-50 text-warning-700 ring-warning-200/70',
  success: 'bg-[#e8f3ec] text-[#1b6a3a] ring-[#cae0d3]/60',
  danger: 'bg-[#fdecec] text-error-700 ring-[#f1c1c1]/60',
};

const TONE_DOT_CLASSES: Record<BadgeTone, string> = {
  neutral: 'bg-neutral-400',
  primary: 'bg-primary-500',
  info: 'bg-accent-ink/70',
  warning: 'bg-warning-500',
  success: 'bg-[#2f8a55]',
  danger: 'bg-error-500',
};

export function Badge({ tone, children, uppercase, icon: Icon, dot, className }: BadgeProps) {
  return (
    <span
      className={cn(
        // whitespace-nowrap protects multi-word labels ("Pending invite",
        // "Lead at all") from breaking mid-chip in dense table cells.
        'text-meta-sm inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 font-medium whitespace-nowrap ring-1',
        TONE_CLASSES[tone],
        uppercase && 'tracking-[0.04em] uppercase',
        className,
      )}
    >
      {dot ? (
        <span
          className={cn('h-1.5 w-1.5 shrink-0 rounded-full', TONE_DOT_CLASSES[tone])}
          aria-hidden="true"
        />
      ) : null}
      {Icon ? <Icon className="h-3 w-3" strokeWidth={1.5} aria-hidden="true" /> : null}
      {children}
    </span>
  );
}
